import { db } from '@util/firebase';
import {
  BlogCommentDocument,
  blogCommentDocumentSchema,
  BlogDocument,
} from '@util/types/firestore/blog';
import {
  collection,
  CollectionReference,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { deleteObject, getStorage, ref } from 'firebase/storage';
import { ApiResponse } from 'models/shared';
import { DeleteCommentArgs, GetBlogCommentsArgs } from './blog.types';

const storage = getStorage();

const blogCommentsRef = collection(
  db,
  'blog_comments'
) as CollectionReference<BlogCommentDocument>;
const blogsRef = collection(db, 'blogs') as CollectionReference<BlogDocument>;

export async function getBlogBySlug(
  slug: string
): Promise<ApiResponse<BlogDocument | null>> {
  const q = query(blogsRef, where('slug', '==', slug), limit(1));
  const snapshot = await getDocs(q);
  const data = snapshot.docs[0]?.data();
  return {
    results: data ?? null,
  };
}

export async function deleteBlog(id: string) {
  const docRef = doc(blogsRef, id);
  return deleteDoc(docRef);
}

export async function deleteBlogImage({
  thumb,
  full,
}: {
  thumb: string;
  full: string;
}) {
  if (!thumb || !full) return;
  // delete thumb and full image
  const thumbRef = ref(storage, thumb);
  const fullRef = ref(storage, full);
  const t = deleteObject(thumbRef);
  const f = deleteObject(fullRef);
  await Promise.all([t, f]);
}

export function getBlogCommenttId(): string {
  const newDocRef = doc(blogCommentsRef);
  const id = newDocRef.id;
  return id;
}

export async function postComment(commentDoc: BlogCommentDocument) {
  const comment_id = getBlogCommenttId();
  const parsedData = blogCommentDocumentSchema.parse({
    ...commentDoc,
    comment_id,
  });
  const docRef = doc(blogCommentsRef, parsedData.comment_id);
  return setDoc(docRef, parsedData);
}

export async function deleteComment(args: DeleteCommentArgs) {
  const docRef = doc(blogCommentsRef, args.commentId);
  deleteDoc(docRef);
}

export async function getBlogComments(
  args: GetBlogCommentsArgs
): Promise<ApiResponse<BlogCommentDocument[]>> {
  const q = query(
    blogCommentsRef,
    where('blog_id', '==', args.blogId),
    where('parent_id', '==', args.parentId),
    orderBy('timestamp', 'desc')
    // infinitei scroll limit?
  );
  const snap = await getDocs(q);
  const results = snap.docs.map((doc) => doc.data());
  return {
    results,
  };
}

export async function createBlog(blog: BlogDocument) {
  // if blog is featured, make sure there are no other featured blogs
  if (blog.is_featured) {
    const q = query(blogsRef, where('is_featured', '==', true));
    const snap = await getDocs(q);
    if (snap.docs.length > 0) {
      // update the other featured blog to not be featured
      const batch = writeBatch(db);
      snap.docs.forEach((featBlog) => {
        const docRef = doc(blogsRef, featBlog.id);
        batch.update(docRef, { is_featured: false });
      });
      await batch.commit();
    }
  }
  const docRef = doc(blogsRef, blog.id);
  return setDoc(docRef, blog);
}

export async function updateBlog(blog: BlogDocument) {
  const docRef = doc(blogsRef, blog.id);
  await updateDoc(docRef, blog);
}

export async function getBlogId(): Promise<string> {
  const newDocRef = doc(blogsRef);
  const id = newDocRef.id;
  return id;
}
